/**
 * Ring buffer of the latest front-end exceptions for the Report Issue link (AIO-373, 3.1).
 * Lives in sessionStorage: survives a page reload, dies with the tab, cleared on logout.
 */
export interface RecentJsError {
    /** ISO 8601 UTC time of the error */
    t: string
    /** error message, truncated */
    m: string
    /** first stack frame (file:line:column), truncated */
    s?: string
    /** page path without query parameters */
    p: string
}

const STORAGE_KEY = 'gpte:recentJsErrors'
const LIMIT = 5
const MAX_TEXT_LENGTH = 200
// The same error usually arrives twice (vue:error + window error) or in a render loop
const DEDUPE_WINDOW_MS = 1000

function read(): RecentJsError[] {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY)
        const list = raw ? JSON.parse(raw) : []
        return Array.isArray(list) ? list : []
    } catch {
        return []
    }
}

function write(list: RecentJsError[]) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list))
    } catch {
        // Storage may be full or unavailable (private mode) — diagnostics must never break the app
    }
}

function truncate(text: string): string {
    return text.length > MAX_TEXT_LENGTH ? `${text.slice(0, MAX_TEXT_LENGTH - 1)}…` : text
}

function messageOf(error: unknown): string {
    if (error instanceof Error) return error.message || error.name
    if (typeof error === 'string') return error
    try {
        return JSON.stringify(error)
    } catch {
        return String(error)
    }
}

function firstStackFrame(error: unknown): string | undefined {
    if (!(error instanceof Error) || !error.stack) return undefined
    // V8: "    at fn (file:line:col)"; Firefox/Safari: "fn@file:line:col"
    const frame = error.stack
        .split('\n')
        .map(line => line.trim())
        .find(line => line.startsWith('at ') || line.includes('@'))
    return frame ? frame.replace(/^at\s+/, '') : undefined
}

export function getRecentJsErrors(): RecentJsError[] {
    if (!import.meta.client) return []
    return read()
}

export function clearRecentJsErrors() {
    if (!import.meta.client) return
    try {
        sessionStorage.removeItem(STORAGE_KEY)
    } catch {
        // see write()
    }
}

export function recordJsError(error: unknown) {
    if (!import.meta.client) return
    try {
        const message = truncate(messageOf(error))
        if (!message) return
        const now = Date.now()
        const list = read()
        const newest = list[0]
        if (newest && newest.m === message && now - Date.parse(newest.t) < DEDUPE_WINDOW_MS) return
        const frame = firstStackFrame(error)
        list.unshift({
            t: new Date(now).toISOString(),
            m: message,
            ...(frame && {s: truncate(frame)}),
            p: window.location.pathname,
        })
        write(list.slice(0, LIMIT))
    } catch {
        // Recording an error must never raise another one
    }
}
